import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Fragment, type ReactNode } from "react";

import { cn } from "@/shared/lib";

import type { HighlightView } from "../../model/Highlight";
import { isOutnumbered } from "../../model/isOutnumbered";
import { HeroFaces } from "./HeroFaces";

const SPAN = msg`in {seconds} s`;
const VERSUS = msg`{us} vs {them}`;
const WIPE = msg`team wipe`;
const DIED = msg`died right after`;
const LOW_HEALTH = msg`survived on {pct}% HP`;
const SHUTDOWN = msg({
  message: "{count, plural, other {ended a #-kill streak}}",
});
const LANE_WITH = msg`with`;
const LANE_KILLED = msg`killed`;
const PARRIES = msg({
  message: "{count, plural, one {# parry} other {# parries}} in a row",
});
const PARRY_THEN = msg`then killed`;
const ENEMIES_DOWN = msg({
  message: "{count, plural, one {# enemy down} other {# enemies down}}",
});
const MIDBOSS_STOLEN = msg`stolen from the enemy`;
const MIDBOSS_CRYSTAL = msg`you took the crystal`;

type HighlightSummaryProps = {
  highlight: HighlightView;
  // Плитка момента: портреты врагов уже на превью, в строке — только числа и напарник.
  compact?: boolean;
  // Первая часть строки (время момента в матче) — через ту же точку, что и остальные.
  lead?: ReactNode;
  className?: string;
};

// Одна строка описания момента: портреты героев и числа — «[Paige][Drifter] за 11 с ·
// 2 против 4 · вайп · выжил на 7 % здоровья». Описание по главному виду момента; коды и числа приходят с сервера.
export const HighlightSummary = ({
  highlight,
  compact = false,
  lead,
  className,
}: HighlightSummaryProps) => {
  const { i18n } = useLingui();
  const { details } = highlight;
  const parts: ReactNode[] = lead === undefined ? [] : [lead];
  switch (highlight.kinds[0]) {
    case "lane_double":
      // В плитке без напарника нечего показать: не оставляем пустую часть с точкой.
      if (compact && details.partner_hero_id === null) {
        break;
      }
      parts.push(
        <span key="lane" className="inline-flex flex-wrap items-center gap-1.5">
          {details.partner_hero_id !== null && (
            <>
              {i18n._(LANE_WITH)}
              <HeroFaces heroIds={[details.partner_hero_id]} />
            </>
          )}
          {!compact && (
            <>
              {i18n._(LANE_KILLED)}
              <HeroFaces heroIds={details.lane_victims} />
            </>
          )}
        </span>,
      );
      break;
    case "parries":
      parts.push(i18n._({ ...PARRIES, values: { count: details.parries } }));
      if (details.parry_victims.length > 0 && !compact) {
        parts.push(
          <span key="then" className="inline-flex items-center gap-1.5">
            {i18n._(PARRY_THEN)}
            <HeroFaces heroIds={details.parry_victims} />
          </span>,
        );
      }
      break;
    case "midboss":
      if (details.midboss !== null) {
        parts.push(i18n._({ ...ENEMIES_DOWN, values: { count: details.midboss.enemy_deaths } }));
        if (details.midboss.stolen) {
          parts.push(i18n._(MIDBOSS_STOLEN));
        }
        if (details.midboss.took_crystal) {
          parts.push(i18n._(MIDBOSS_CRYSTAL));
        }
      }
      break;
    case "ultimate":
      if (!compact) {
        parts.push(<HeroFaces key="hits" heroIds={details.ult_hits} />);
      }
      parts.push(i18n._({ ...ENEMIES_DOWN, values: { count: details.ult_victims.length } }));
      break;
    default:
      if (!compact) {
        parts.push(
          <span key="victims" className="inline-flex items-center gap-1.5">
            <HeroFaces heroIds={details.victims} />
            {details.span_s > 0 && i18n._({ ...SPAN, values: { seconds: details.span_s } })}
          </span>,
        );
      } else if (details.span_s > 0) {
        parts.push(i18n._({ ...SPAN, values: { seconds: details.span_s } }));
      }
      if (isOutnumbered(details)) {
        parts.push(
          i18n._({
            ...VERSUS,
            values: { us: (details.allies_near ?? 0) + 1, them: details.enemies_near ?? 0 },
          }),
        );
      }
      if (details.wipe) {
        parts.push(i18n._(WIPE));
      }
      if (details.low_health_pct !== null) {
        parts.push(i18n._({ ...LOW_HEALTH, values: { pct: details.low_health_pct } }));
      }
      if (details.shutdown_streak > 0) {
        parts.push(i18n._({ ...SHUTDOWN, values: { count: details.shutdown_streak } }));
      }
      if (details.died) {
        parts.push(i18n._(DIED));
      }
  }
  // В плитке строка узкая: обычный текст переносится по словам, и точка не повисает в конце
  // строки отдельным куском.
  if (compact) {
    return (
      <span className={className}>
        {parts.map((part, index) => (
          <Fragment key={index}>
            {index > 0 && " · "}
            {part}
          </Fragment>
        ))}
      </span>
    );
  }
  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-1.5 gap-y-1", className)}>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && <span aria-hidden="true">·</span>}
          {typeof part === "string" ? <span>{part}</span> : part}
        </Fragment>
      ))}
    </span>
  );
};
